// 影響預覽對話框（SPEC #org-tree-ui 第 6 點、#org-tree-preview-fields）：移動節點、搬家戶（單筆 / 批次）、開關中斷繼承、撤銷節點管理員、委派縮小 / 撤銷共用。
//   - 只在按〔下一步〕〔移動…〕〔開啟中斷繼承〕這類按鈕時打預覽（打開這個對話框 = 按了），不跟著勾選即時打
//   - 三段：會失去 / 會得到 / 仍有存取；「仍有存取」只預設勾 granted_by_losing（選單 20），勾選不連動
//   - 主按鈕上方黃字「之後仍有 N 人看得到 <目標>」依目前勾選即時算（不重打預覽）
//   - self_gain → 理由必填 + 自我授權黃框；reason_required（擴大到高風險委派）→ 理由必填、送出立即生效（platform#90，不設第二位核可）
//   - 送出 409 preview_stale → 自動重新預覽、把變了的列標出來、不清掉已選的；429 → 「預覽太頻繁，N 秒後再試」
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../../components/Dialog';
import type { Preview, PreviewPerson } from '../../lib/orgTreeApi';
import { actionWords, changedRows, defaultAlsoRevoke, keepChecked, normPreview, peopleCount, personKey, stillVisible } from '../../lib/orgTreeUi';
import { isPreviewStale, otErr, retryAfter } from './otErr';
import { stamp } from '../../lib/timefmt';

export interface PreviewSubmit {
  token: string;
  alsoRevoke: string[];
  reason?: string;
  preview: Preview;
}

export function PreviewDialog(props: {
  open: boolean;
  title: string;
  intro?: ReactNode;
  /** 「之後仍有 N 人看得到 <target>」 */
  target: string;
  load(): Promise<Preview>;
  submit(p: PreviewSubmit): Promise<unknown>;
  /** 主按鈕（動作本身），自動加「（需要重新驗證）」 */
  action: string;
  /** 勾了 N 筆一併撤銷時的主按鈕 */
  actionWithRevoke?: (n: number) => string;
  /** 自我授權時的主按鈕 */
  selfAction?: string;
  /** 段落下面的固定說明（例：之後關閉中斷繼承從關閉時起算） */
  note?: ReactNode;
  /** 「會失去」的標題（委派撤銷寫「<受託方> 會失去」） */
  losingTitle?: (n: { people: number; orgs: number }) => string;
  /** 自我授權黃框（你會因此取得 … 的影像） */
  selfText?: string;
  /** 理由選填（撤銷委派、撤銷指派） */
  reasonOptional?: boolean;
  onDone(r: unknown): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const { open, load, submit, onClose, onDone } = props;
  const [p, setP] = useState<Preview | null>(null);
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  async function run(prev: Preview | null, keep: Set<string> | null) {
    setLoading(true);
    setErr(null);
    try {
      const np = normPreview(await load());
      setP(np);
      setChanged(prev ? changedRows(prev, np) : new Set());
      setChecked(keep ? keepChecked(keep, np) : defaultAlsoRevoke(np.remaining_access));
    } catch (e) {
      setErr(retryAfter(e) ? t('ot.err_rate_limited', { count: retryAfter(e) }) : otErr(t, e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) {
      setP(null);
      setReason('');
      void run(null, null);
    }
    // 只在打開時預覽一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visible = useMemo(() => stillVisible(p?.remaining_access, checked), [p, checked]);
  const lose = peopleCount(p?.losing);
  const needReason = (!!p?.self_gain && !props.reasonOptional) || !!p?.reason_required;
  const nRevoke = checked.size;

  async function go() {
    if (!p) return;
    if (needReason && !reason.trim()) {
      setErr(t('org.err_REASON_REQUIRED'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await submit({ token: p.preview_token, alsoRevoke: [...checked], reason: reason.trim() || undefined, preview: p });
      onDone(r);
    } catch (e) {
      if (isPreviewStale(e)) {
        setErr(t('ot.preview_restaled'));
        await run(p, checked);
      } else setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }

  const fmt = (iso?: string) => (iso ? stamp(iso) : '');
  const row = (kind: 'g' | 'l', x: PreviewPerson, i: number) => (
    <tr key={kind + i} className={changed.has(personKey(kind, x)) ? 'ot-changed' : undefined} data-preview-row={kind}>
      <td data-label={t('ot.col_who')}>
        {x.principal_kind === 'group' ? '▦ ' : ''}
        {x.display_name}
        {x.principal_kind === 'group' && x.member_count !== undefined ? ' ' + t('ot.n_people_paren', { n: x.member_count }) : ''}
        {x.principal_kind === 'org' && <span className="badge ot-dlg-tag">⤳ {t('ot.delegation')}</span>}
        {x.is_self && <span className="badge badge-warn">{t('ot.you')}</span>}
        {kind === 'l' && x.other_paths && <span className="badge badge-warn">{t('ot.other_paths')}</span>}
      </td>
      <td data-label={t('ot.col_role')}>{x.principal_kind === 'org' ? t('ot.n_grantees', { n: x.member_count ?? 0 }) : x.role ? t('ot.role_' + x.role, { defaultValue: x.role }) : ''}</td>
      <td data-label={x.lost_actions ? t('ot.col_lost_actions') : t('ot.col_from')}>{x.lost_actions ? actionWords(t, x.lost_actions) : (x.from_node_name ?? '')}</td>
    </tr>
  );

  const primary =
    p?.self_gain && props.selfAction
      ? props.selfAction
      : nRevoke && props.actionWithRevoke
        ? props.actionWithRevoke(nRevoke)
        : props.action;

  return (
    <Dialog open={open} title={props.title} onClose={onClose} wide>
      <div className="ot-preview" data-testid="impact-preview">
        {props.intro && <div className="muted">{props.intro}</div>}
        {loading && <p className="muted">{t('ot.previewing')}</p>}
        {p && (
          <>
            {(p.losing.length > 0 || (p.total_losing ?? 0) > 0) && (
              <section>
                <h3 className="ot-h3">{props.losingTitle ? props.losingTitle(lose) : t('ot.losing_title', { n: lose.people, orgs: lose.orgs })}</h3>
                <div className="table-wrap">
                  <table className="grid ot-cards">
                    <thead>
                      <tr>
                        <th>{t('ot.col_who')}</th>
                        <th>{t('ot.col_role_before')}</th>
                        <th>{p.losing.some((x) => x.lost_actions) ? t('ot.col_lost_actions') : t('ot.col_from')}</th>
                      </tr>
                    </thead>
                    <tbody>{p.losing.map((x, i) => row('l', x, i))}</tbody>
                  </table>
                </div>
                {(p.total_losing ?? 0) > p.losing.length && <p className="muted small">{t('ot.more_rows', { n: (p.total_losing ?? 0) - p.losing.length })}</p>}
              </section>
            )}
            {(p.homes_affected !== undefined || p.active_streams !== undefined) && (
              <p className="small" data-testid="impact-scope">
                {t('ot.impact_scope', { homes: p.homes_affected ?? 0, devices: p.devices_affected ?? 0 })}
                {p.active_streams !== undefined && ' · ' + t('ot.active_streams', { n: p.active_streams })}
              </p>
            )}
            {p.gaining.length > 0 && (
              <section>
                <h3 className="ot-h3">{t('ot.gaining_title', { n: peopleCount(p.gaining).people })}</h3>
                <div className="table-wrap">
                  <table className="grid ot-cards">
                    <thead>
                      <tr>
                        <th>{t('ot.col_who')}</th>
                        <th>{t('ot.col_role')}</th>
                        <th>{t('ot.col_from')}</th>
                      </tr>
                    </thead>
                    <tbody>{p.gaining.map((x, i) => row('g', x, i))}</tbody>
                  </table>
                </div>
                <p className="ot-info small">{t('ot.history_from', { time: fmt(p.gaining[0]?.history_visible_from) || t('ot.now') })}</p>
              </section>
            )}
            {p.delegations.length > 0 && (
              <section data-testid="impact-delegations">
                {p.delegations.map((d) => (
                  <p key={d.delegation_id} className={d.change === 'gained' ? 'ot-warn small' : 'small'}>
                    {d.change === 'gained' ? t('ot.dlg_gained', { org: d.to_org_name, n: d.homes ?? 0 }) : t('ot.dlg_lost', { org: d.to_org_name, n: d.homes ?? 0 })}
                    {d.reason_required && ' ' + t('ot.high_risk_reason_note')}
                  </p>
                ))}
              </section>
            )}
            {(p.remaining_access?.length ?? 0) > 0 && (
              <section data-testid="remaining-access">
                <h3 className="ot-h3">{t('ot.remaining_title')}</h3>
                <div className="table-wrap">
                  <table className="grid ot-cards">
                    <thead>
                      <tr>
                        <th>{t('ot.col_who')}</th>
                        <th>{t('ot.col_how')}</th>
                        <th>{t('ot.col_scope')}</th>
                        <th>{t('ot.col_granted_by')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {p.remaining_access!.map((r) => (
                        <tr key={r.ref_id} className={changed.has('r:' + r.ref_id) ? 'ot-changed' : undefined} data-remaining={r.ref_id}>
                          <td data-label={t('ot.col_who')}>{r.display_name}</td>
                          <td data-label={t('ot.col_how')}>{r.detail ?? t('ot.path_' + r.kind, { defaultValue: t('common.state_unknown') })}</td>
                          <td data-label={t('ot.col_scope')}>{r.scope_label ?? ''}</td>
                          <td className="small" data-label={t('ot.col_granted_by')}>
                            {r.granted_by_name ?? t('common.none')}
                            {r.target_is_losing && <strong> · {t('ot.target_still', { name: r.display_name })}</strong>}
                            {!r.target_is_losing && r.granted_by_other_path && ' · ' + t('ot.granter_still', { path: r.granted_by_other_path })}
                          </td>
                          <td>
                            {r.keep || r.revocable === false ? (
                              <span className="muted small">{t('ot.keep')}</span>
                            ) : (
                              <label className="row-inline small">
                                <input
                                  type="checkbox"
                                  checked={checked.has(r.ref_id)}
                                  onChange={(e) => {
                                    const n = new Set(checked);
                                    if (e.target.checked) n.add(r.ref_id);
                                    else n.delete(r.ref_id);
                                    setChecked(n);
                                  }}
                                />
                                {t('ot.also_revoke')}
                              </label>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {(p.granted_by_target?.length ?? 0) > 0 && (
              <section data-testid="granted-by-target">
                <h3 className="ot-h3">{t('ot.granted_by_target_title')}</h3>
                <ul className="plain-list">
                  {p.granted_by_target!.map((a) => (
                    <li key={a.assignment_id}>
                      <label className="row-inline">
                        <input
                          type="checkbox"
                          checked={checked.has(a.assignment_id)}
                          onChange={(e) => {
                            const n = new Set(checked);
                            if (e.target.checked) n.add(a.assignment_id);
                            else n.delete(a.assignment_id);
                            setChecked(n);
                          }}
                        />
                        {a.display_name} · {t('ot.role_' + a.role, { defaultValue: a.role })} · {(a.node_path ?? []).join(' › ')} · {t('ot.also_revoke')}
                      </label>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {props.note && <p className="ot-info small">{props.note}</p>}
            {p.reason_required && <p className="ot-warn small" data-testid="high-risk-reason">{t('ot.reason_required_body')}</p>}
            {p.self_gain && (
              <>
                <p className="ot-warn small" data-testid="self-gain">
                  {props.selfText ?? t('ot.self_gain_generic')}
                </p>
              </>
            )}
            {(p.self_gain || p.reason_required || props.reasonOptional) && (
              <label className="field">
                <span>
                  {t('ot.reason')}
                  {needReason ? ' *' : ` ${t('ot.optional')}`}
                </span>
                <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
              </label>
            )}
            {visible.length > 0 && (p.remaining_access?.length ?? 0) > 0 && (
              <p className="ot-warn small" data-testid="still-visible">
                {t('ot.still_visible', { n: visible.length, target: props.target, who: visible.map((v) => (v.how ? `${v.name}${t('common.paren', { x: v.how })}` : v.name)).join(t('common.list_sep')) })}
              </p>
            )}
          </>
        )}
        {err && (
          <p className="error" role="alert">
            {err}
          </p>
        )}
        <div className="dialog-actions">
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {!p && !loading && (
            <button className="btn" onClick={() => void run(null, null)}>
              {t('ot.preview_again')}
            </button>
          )}
          <button className="btn btn-primary" disabled={!p || busy || loading || (needReason && !reason.trim())} onClick={() => void go()} data-testid="preview-submit">
            {primary}
            {t('ot.reauth_suffix')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
